import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { useNavigate, useParams } from "react-router";
import { useNotification } from "providers/Notification";
import DataTable, { useTableColumnState } from "components/Table";
import StatusLabel, { StatusColor } from "components/StatusLabel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { formatDateTime, fromUnix } from "helpers/dateFormat";
import { storedRoleLabel } from "helpers/roles";

interface AdminLoginModel {
    items: AdminLoginItem[]
}

interface AdminLoginItem {
    id: string
    username: string
    authentication: string
    role: string
    ipAddress: string
    userAgent: string
    loginTimestamp: Date
    expirationTimestamp: Date
    logoutTimestamp: Date
    status: string
}

type columnsType = "authentication" | "role" | "ipAddress" | "userAgent" | "loginTimestamp" | "status" | "expirationTimestamp";

export default function AdminUserSessions() {
    const navigate = useNavigate();
    // react-router already decodes dynamic route segments, so useParams()
    // gives us the plain username - decoding it again here would corrupt
    // any username containing a literal "%" or other percent-encoded byte.
    const { username = "" } = useParams<{ username: string }>();
    const login = useLogin();
    const notification = useNotification();
    const [finishedLoading, setFinishedLoading] = useState(false);

    const [skipItems, setSkipItems] = useState(0);
    const [takeCount, setTakeCount] = useState(25);
    const [dataCount, setDataCount] = useState(0);
    const [activeOnly, setActiveOnly] = useState(false);

    // The list is always most-recent-login-first, straight from the
    // backend's per-user index - there's no arbitrary column sorting here.
    const [columnsState, setColumnsState] = useTableColumnState<columnsType>([
        { columnId: "authentication", width: "10%" },
        { columnId: "role", width: "10%" },
        { columnId: "ipAddress", width: "15%" },
        { columnId: "userAgent", width: "20%" },
        { columnId: "loginTimestamp", width: "15%" },
        { columnId: "status", width: "10%" },
        { columnId: "expirationTimestamp", width: "20%" },
    ]);

    const [listState, setListState] = useState<AdminLoginModel>({ items: [] });

    const req = login.GetAxios();

    // Guards against out-of-order responses: if the user changes filters/
    // page again before an earlier request resolves, only the latest
    // request's response is allowed to update state.
    const latestRequestId = useRef(0);

    const loadData = async (skipItems: number, takeCount: number, activeOnly: boolean) => {
        const requestId = ++latestRequestId.current;
        setFinishedLoading(false);
        if (!req) {
            setFinishedLoading(true);
            return; //not logged in, nothing to do
        }
        try {
            const resp = await req.get("/api/adminlogins", {
                params: {
                    username,
                    skip: skipItems,
                    take: takeCount,
                    active: activeOnly || undefined,
                }
            });

            if (requestId !== latestRequestId.current) {
                return; // a newer request started; ignore this stale response
            }

            setSkipItems(skipItems);
            setTakeCount(takeCount);
            setActiveOnly(activeOnly);
            setListState({
                items: (resp.data.items || []).map((x: any) => {
                    const item: AdminLoginItem = {
                        id: x.id,
                        username: x.username,
                authentication: x.authentication,
                        role: x.role || "",
                        ipAddress: x.ipAddress,
                        userAgent: x.userAgent,
                        loginTimestamp: fromUnix(x.loginTimestamp),
                        expirationTimestamp: fromUnix(x.expirationTimestamp),
                        logoutTimestamp: fromUnix(x.logoutTimestamp),
                        status: x.status,
                    };
                    return item;
                })
            });
            setDataCount(resp.data.count);
        } catch (err) {
            if (requestId !== latestRequestId.current) {
                return; // a newer request started; ignore this stale response
            }

            if (IsUnauthorizedError(err)) {
                return;
            }

            notification.Display({
                title: "Error",
                message: "Could not fetch admin login data",
                type: "error",
            });
        } finally {
            if (requestId === latestRequestId.current) {
                setFinishedLoading(true);
            }
        }
    };

    useEffect(() => {
        // Reset to the first page for the new user - a stale skip offset
        // left over from a previous user's pagination wouldn't make sense
        // for someone else's session history.
        loadData(0, takeCount, activeOnly);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    const renderStatus = (status: string) => {
        let color: StatusColor = "error";
        if (status === "Active") color = "success";
        else if (status === "Logged Out") color = "disabled";

        return <StatusLabel label={status} color={color} />;
    };

    return <>
        <Box display="flex" alignItems="center" marginBottom="10px">
            <Stack direction="row" alignItems="center" gap={2}>
                <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate("/users", { state: { tab: "adminusers" } })}>Administrators</Button>
                <Typography variant="h1">Sessions for {username}</Typography>
            </Stack>
        </Box>

        <Stack direction={"row"} gap={1} marginBottom={"10px"} marginTop={"10px"}>
            <Stack flexGrow={1} direction={"row"} justifyContent={"end"}>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="active-only-checkbox"
                            checked={activeOnly}
                            onChange={(x) => loadData(0, takeCount, x.target.checked)}
                        />
                    }
                    label="Active only"
                />
            </Stack>
        </Stack>
        <DataTable
            sx={{
                maxHeight: "90%",
            }}
            size="medium"
            columnsDataId={"adminUserSessionsTablev1"}
            data={listState.items}
            dataCount={dataCount}
            finishedLoading={finishedLoading}
            paginationStartItem={skipItems}
            paginationItemCount={takeCount}
            onPaginationStartItemChanged={(x) => {
                loadData(x, takeCount, activeOnly);
            }}
            onPaginationItemCountChanged={(x) => {
                loadData(skipItems, x, activeOnly);
            }}
            onColumnOrderChanged={(x) => setColumnsState(x)}
            columnsState={columnsState}
            columns={{
                authentication: {
                    displayName: "Authentication",
                    sortable: false,
                    render: (x) => {
                        return x.authentication === "saml" ? "SAML" : "Password";
                    },
                },
                // NS-753
                role: {
                    displayName: "Role",
                    sortable: false,
                    render: (x) => {
                        return storedRoleLabel(x.role);
                    },
                },
                ipAddress: {
                    displayName: "IP Address",
                    sortable: false,
                    render: (x) => {
                        return x.ipAddress;
                    },
                },
                userAgent: {
                    displayName: "User Agent",
                    sortable: false,
                    render: (x) => {
                        return x.userAgent;
                    },
                },
                loginTimestamp: {
                    displayName: "Login Time",
                    sortable: false,
                    render: (x) => {
                        return formatDateTime(x.loginTimestamp);
                    },
                },
                status: {
                    displayName: "Status",
                    sortable: false,
                    render: (x) => {
                        return renderStatus(x.status);
                    },
                },
                expirationTimestamp: {
                    displayName: "Logout / Expiration",
                    sortable: false,
                    render: (x) => {
                        const timestamp = x.status === "Logged Out" ? x.logoutTimestamp : x.expirationTimestamp;
                        return formatDateTime(timestamp);
                    },
                },
            }}
        />
    </>;
}
